/**
 * De API antwoordt bij fouten met RFC 7807 problem+json:
 *
 * ```json
 * {
 *   "type": "https://smartkeur.nl/problems/validation",
 *   "title": "De invoer klopt niet",
 *   "status": 422,
 *   "detail": "Controleer de gemarkeerde velden.",
 *   "instance": "/api/v1/inspections/42",
 *   "errors": { "scheduled_at": ["Dit veld is verplicht."] }
 * }
 * ```
 *
 * Alles wat daarvan afwijkt (kale Laravel-JSON, HTML, een lege body) wordt
 * alsnog naar dit model vertaald, zodat de UI maar één fouttype kent.
 */
export interface ProblemDetails {
    type: string;
    title: string;
    status: number;
    detail?: string;
    instance?: string;
    /** Veldnaam -> meldingen. Laravel levert dit bij 422. */
    errors?: Record<string, string[]>;
    [key: string]: unknown;
}

export type ApiErrorKind =
    | 'offline'
    | 'network'
    | 'timeout'
    | 'unauthorized'
    | 'forbidden'
    | 'notFound'
    | 'validation'
    | 'conflict'
    | 'rateLimited'
    | 'client'
    | 'server';

const MESSAGES: Record<ApiErrorKind, string> = {
    offline: 'Je bent offline. De wijziging staat klaar en gaat mee zodra er verbinding is.',
    network: 'Geen verbinding met de server.',
    timeout: 'De server reageerde niet op tijd.',
    unauthorized: 'Je sessie is verlopen. Log opnieuw in.',
    forbidden: 'Je hebt geen toegang tot dit onderdeel.',
    notFound: 'Niet gevonden.',
    validation: 'De invoer klopt niet.',
    conflict: 'Deze gegevens zijn ondertussen gewijzigd.',
    rateLimited: 'Te veel verzoeken achter elkaar. Probeer het zo opnieuw.',
    client: 'Het verzoek kon niet worden verwerkt.',
    server: 'Er ging iets mis op de server.',
};

export function kindForStatus(status: number): ApiErrorKind {
    switch (status) {
        case 401:
            return 'unauthorized';
        case 403:
            return 'forbidden';
        case 404:
            return 'notFound';
        case 409:
        case 412:
            return 'conflict';
        case 422:
            return 'validation';
        case 429:
            return 'rateLimited';
        default:
            return status >= 500 ? 'server' : 'client';
    }
}

/** Eén fouttype voor de hele app. */
export class ApiError extends Error {
    readonly kind: ApiErrorKind;
    readonly status: number;
    readonly problem: ProblemDetails;
    readonly retryable: boolean;
    readonly retryAfterMs: number | undefined;

    constructor(input: {
        kind: ApiErrorKind;
        status: number;
        problem: ProblemDetails;
        message?: string;
        retryAfterMs?: number;
        cause?: unknown;
    }) {
        super(
            input.message ?? input.problem.detail ?? input.problem.title ?? MESSAGES[input.kind],
            {
                cause: input.cause,
            },
        );

        this.name = 'ApiError';
        this.kind = input.kind;
        this.status = input.status;
        this.problem = input.problem;
        this.retryAfterMs = input.retryAfterMs;
        this.retryable =
            input.kind === 'network' ||
            input.kind === 'timeout' ||
            input.kind === 'rateLimited' ||
            input.kind === 'server';
    }

    /** Veldfouten uit een 422, klaar voor een formulier. */
    get validationErrors(): Record<string, string[]> {
        return this.problem.errors ?? {};
    }

    firstValidationError(field: string): string | undefined {
        return this.validationErrors[field]?.[0];
    }

    static fromProblem(status: number, problem: ProblemDetails, retryAfterMs?: number): ApiError {
        const kind = kindForStatus(status);

        return new ApiError({
            kind,
            status,
            problem,
            ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
        });
    }

    static offline(path: string): ApiError {
        return new ApiError({
            kind: 'offline',
            status: 0,
            problem: problemFor('offline', 0, MESSAGES.offline, path),
        });
    }

    static network(path: string, cause?: unknown): ApiError {
        return new ApiError({
            kind: 'network',
            status: 0,
            problem: problemFor('network', 0, MESSAGES.network, path),
            cause,
        });
    }

    static timeout(path: string, timeoutMs: number): ApiError {
        return new ApiError({
            kind: 'timeout',
            status: 0,
            problem: problemFor('timeout', 0, `${MESSAGES.timeout} (${timeoutMs} ms)`, path),
        });
    }
}

export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

/**
 * De zin die een scherm onder een mislukte actie zet. Bij een 422 is dat de
 * eerste veldmelding van de API ("Geef een reden van minimaal 10 tekens"), niet
 * de algemene kop; verder de `detail` van het probleem, of een vaste zin.
 */
export function errorMessage(error: unknown): string {
    if (isApiError(error)) {
        const [firstField] = Object.values(error.validationErrors);

        return firstField?.[0] ?? error.message;
    }

    return error instanceof Error && error.message !== '' ? error.message : 'Er ging iets mis.';
}

/** Het laatste deel van het `type` van een probleem: `invalid-status-transition`. */
export function problemKey(error: unknown): string | null {
    if (!isApiError(error)) {
        return null;
    }

    return error.problem.type.split('/').filter(Boolean).pop() ?? null;
}

function problemFor(
    kind: ApiErrorKind,
    status: number,
    detail: string,
    instance: string,
): ProblemDetails {
    return {
        type: `https://smartkeur.nl/problems/${kind}`,
        title: MESSAGES[kind],
        status,
        detail,
        instance,
    };
}

/** De leden die problem+json zelf definieert; al het andere is een extensie van het endpoint. */
const PROBLEM_MEMBERS = new Set([
    'type',
    'title',
    'status',
    'detail',
    'instance',
    'errors',
    'message',
]);

/**
 * Vertaalt een foutbody naar problem+json. Accepteert echte problem-details,
 * kale Laravel-fouten (`{ message, errors }`) en onzin.
 *
 * Extensieleden (RFC 7807 §3.2) blijven staan: een verlopen klantlink stuurt
 * `expired_at` en de briefkop van het bedrijf mee, een foute toegangscode
 * `attempts_left`. Zonder die leden tekent de klantpagina zulke foutpagina's
 * zonder huisstijl en zonder datum.
 */
export function toProblemDetails(status: number, body: unknown, instance: string): ProblemDetails {
    const kind = kindForStatus(status);
    const fallback: ProblemDetails = {
        type: `https://smartkeur.nl/problems/${kind}`,
        title: MESSAGES[kind],
        status,
        instance,
    };

    if (typeof body === 'string' && body.trim() !== '') {
        return { ...fallback, detail: body.slice(0, 500) };
    }

    if (!body || typeof body !== 'object') {
        return fallback;
    }

    const record = body as Record<string, unknown>;
    const problem: ProblemDetails = { ...fallback };

    for (const [member, value] of Object.entries(record)) {
        if (!PROBLEM_MEMBERS.has(member)) {
            problem[member] = value;
        }
    }

    if (typeof record.type === 'string') {
        problem.type = record.type;
    }

    if (typeof record.title === 'string') {
        problem.title = record.title;
    }

    if (typeof record.status === 'number') {
        problem.status = record.status;
    }

    if (typeof record.instance === 'string') {
        problem.instance = record.instance;
    }

    if (typeof record.detail === 'string') {
        problem.detail = record.detail;
    } else if (typeof record.message === 'string' && record.message !== '') {
        problem.detail = record.message;
    }

    if (record.errors && typeof record.errors === 'object') {
        problem.errors = normalizeValidationErrors(record.errors as Record<string, unknown>);
    }

    return problem;
}

function normalizeValidationErrors(errors: Record<string, unknown>): Record<string, string[]> {
    return Object.fromEntries(
        Object.entries(errors).map(([field, value]) => [
            field,
            Array.isArray(value) ? value.map(String) : [String(value)],
        ]),
    );
}
